import { Alert, Box, Combobox, FormField, FormHelp, Icon, Input, Label, Text } from '@zillow/constellation-10';
import { IconSearchFilled } from '@zillow/constellation-icons';

import { UNIT_ALERT } from '../../../../constants/AddPropertyConstants';
import PropertyConstants from '../../../../constants/PropertyConstants';

import type { ChangeEvent, ReactElement, ReactNode, RefObject } from 'react';
import type { ComboboxOptionType } from '@zillow/constellation-10';
import type { useAddressAutocomplete } from '../../../../hooks/queries/address';
import type { UnitAlertValue } from '../../../../constants/AddPropertyConstants';

export type { AddPropertyDetailsState } from '../../../../hooks/useAddPropertyDetailsForm';

const {
  PROPERTY_TYPES: { COMMUNITY, HOUSE, MULTIPLEX, TOWNHOUSE, CONDO },
} = PropertyConstants;

type Props = {
  address: string;
  propertyType: string;
  unit: string;
  numUnits: string;
  addressError: string;
  propertyTypeError: string;
  numUnitsError: string;
  propertyAlreadyPostedWarningMessage: string | Array<ReactNode>;
  unitAlert: UnitAlertValue | null;
  isPropertyTypeDisabled: boolean;
  addressInputRef: RefObject<HTMLInputElement>;
  queryData: ReturnType<typeof useAddressAutocomplete>[1];
  handleAddressChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleSelectAddress: (option: ComboboxOptionType) => void;
  handlePropertyTypeChange: (value: string) => void;
  handleUnitChange: (e: ChangeEvent<HTMLInputElement>) => void;
  handleNumUnitsChange: (e: ChangeEvent<HTMLInputElement>) => void;
};

const AddPropertyDetailsModal = ({
  address,
  propertyType,
  unit,
  numUnits,
  addressError,
  propertyTypeError,
  numUnitsError,
  propertyAlreadyPostedWarningMessage,
  unitAlert,
  isPropertyTypeDisabled,
  addressInputRef,
  queryData,
  handleAddressChange,
  handleSelectAddress,
  handlePropertyTypeChange,
  handleUnitChange,
  handleNumUnitsChange,
}: Props): ReactElement => {
  const renderSecondaryField = () => {
    switch (propertyType) {
      case COMMUNITY:
        return (
          <FormField
            error={!!numUnitsError}
            required
            label={<Label>Number of units</Label>}
            control={
              <Input
                data-testid="flow-num-units"
                aria-label="Number of units"
                autoComplete="off"
                onChange={handleNumUnitsChange}
                value={numUnits}
              />
            }
            description={
              numUnitsError ? (
                <FormHelp data-testid="flow-numUnits-error">{numUnitsError}</FormHelp>
              ) : (
                <FormHelp>Total number of units in the community</FormHelp>
              )
            }
          />
        );
      case MULTIPLEX:
        return (
          <FormField
            error={!!numUnitsError}
            required
            label={<Label>Number of units you own or manage</Label>}
            control={
              <Input
                data-testid="flow-num-units"
                aria-label="Number of units you own or manage"
                autoComplete="off"
                onChange={handleNumUnitsChange}
                value={numUnits}
              />
            }
            description={
              numUnitsError ? (
                <FormHelp data-testid="flow-numUnits-error">{numUnitsError}</FormHelp>
              ) : (
                <FormHelp>Enter the number of units in your multiplex</FormHelp>
              )
            }
          />
        );
      default:
        return (
          <FormField
            label={
              <Label>
                Unit number{' '}
                <Text textStyle="body-sm" css={{ color: 'text.subtle' }}>
                  (optional)
                </Text>
              </Label>
            }
            control={
              <Input data-testid="flow-unit-number" aria-label="Unit number" onChange={handleUnitChange} value={unit} />
            }
            description={<FormHelp>Enter apartment, suite, or unit number</FormHelp>}
          />
        );
    }
  };

  return (
    <Box css={{ display: 'flex', flexDirection: 'column', gap: '400' }}>
      <Text textStyle="body">
        Add a property to list it for free on Zillow, Trulia, and HotPads. You can also add properties that already have
        tenants so you can use Zillow Rental’s payments, leases, and renter management services.
      </Text>

      {Boolean(propertyAlreadyPostedWarningMessage) && <Alert>{propertyAlreadyPostedWarningMessage}</Alert>}

      {unitAlert === UNIT_ALERT.VALIDATED_WITHOUT_UNIT && (
        <Alert>
          We are unable to validate the property address with this unit number. Double-check before creating the
          property.
        </Alert>
      )}
      {unitAlert === UNIT_ALERT.PROMPT_FOR_UNIT && (
        <Alert>
          We’ve detected this address may have a unit number. If your property has one, add it below. Otherwise, you can
          leave it blank.
        </Alert>
      )}

      <FormField
        error={!!addressError}
        required
        label={<Label id="flow-street-address-label">Address</Label>}
        control={
          <Combobox
            css={{ boxShadowColor: 'border.subtle', _hover: { boxShadowColor: 'border.action.neutral.hover' } }}
            appearance="input"
            focusFirstOption={true}
            options={
              queryData?.data?.addresses?.map((addr) => ({
                ...addr,
                value: addr.name,
                label: addr.name,
              })) ?? []
            }
            onClear={() => handleAddressChange({ target: { value: '' } } as ChangeEvent<HTMLInputElement>)}
            onOptionSelect={handleSelectAddress}
            selectCallbackFrequency="always"
            renderInputGroup={({ ref: _ref, children, ...props }) => (
              <Combobox.InputGroup {...props}>
                <Icon size="sm" css={{ color: 'icon.subtle', flexShrink: 0 }} render={<IconSearchFilled />} />
                {children}
              </Combobox.InputGroup>
            )}
            renderInput={({ ...rest }) => (
              <Combobox.Input
                {...rest}
                ref={addressInputRef}
                data-testid="flow-autocomplete"
                value={address}
                onChange={handleAddressChange}
                aria-labelledby="flow-street-address-label"
              />
            )}
            renderEmptyState={() => address.length <= 7 && null}
            freeForm={true}
            autoCompleteBehavior="none"
          />
        }
        description={
          addressError ? (
            <FormHelp>{addressError}</FormHelp>
          ) : (
            <FormHelp>Enter a USPS-validated address, including street, city, state, and 9-digit ZIP code</FormHelp>
          )
        }
      />

      <Box css={{ display: 'grid', gridTemplateColumns: { base: '1fr', md: 'repeat(2, 1fr)' }, gap: '400' }}>
        <FormField
          error={!!propertyTypeError}
          required
          label={<Label>Property type</Label>}
          control={
            <div data-testid="flow-propertyType-select">
              <Combobox
                css={{ boxShadowColor: 'border.subtle', _hover: { boxShadowColor: 'border.action.neutral.hover' } }}
                appearance="select"
                aria-label="Property type"
                autoCompleteBehavior="automatic"
                disabled={isPropertyTypeDisabled}
                fluidDropdown
                limitChipsShown={1}
                placeholder="Select property type"
                selectCallbackFrequency="change"
                shouldAwaitInteractionResponse={true}
                showLabelForValue={true}
                size="md"
                value={propertyType}
                options={[
                  { label: HOUSE, meta: 'Single detached house', value: HOUSE },
                  { label: TOWNHOUSE, meta: 'Multi-level home', value: TOWNHOUSE },
                  { label: 'Condo', meta: 'Single unit in a building', value: CONDO },
                  { label: MULTIPLEX, meta: '(duplex to 24 units)', value: MULTIPLEX },
                  { label: COMMUNITY, meta: '(25+ units)', value: COMMUNITY },
                ]}
                onChange={handlePropertyTypeChange}
              />
            </div>
          }
          description={
            propertyTypeError ? (
              <FormHelp data-testid="flow-propertyType-error">{propertyTypeError}</FormHelp>
            ) : (
              <FormHelp>Select the property type that best matches yours</FormHelp>
            )
          }
        />

        {renderSecondaryField()}
      </Box>
    </Box>
  );
};

export default AddPropertyDetailsModal;
